import type { ProductFieldKey, ProductForm } from './types'

/**
 * Кассовые поля общего товара словами магазина (RP-SY3, SYNC-STAR §2). Коды — те же, что хранит Platform: ставка — код словаря
 * чека (`VatRates`), предмет расчёта — тег 1212 ФФД (1–33), способ расчёта — тег 1214 (1–7). Пустое поле — «не знаю»: это
 * отдельное состояние, а не «без НДС», «товар» или «полный расчёт»; своя касса такой товар не продаёт, пока поле не определено.
 */

export interface CashOption {
  value: string
  label: string
}

/** Подпись пустого значения кассового поля — во всех списках и в сводке одна. */
export const UNKNOWN_LABEL = 'Не знаю (не определено)'

/** Ставки, которые можно выбрать (словарь чека). 18/118 устарела: её видно у старого товара, но заново не выбрать. */
const VAT_OPTIONS: readonly CashOption[] = [
  { value: 'none', label: 'Без НДС' },
  { value: 'vat0', label: 'НДС 0 %' },
  { value: 'vat5', label: 'НДС 5 %' },
  { value: 'vat7', label: 'НДС 7 %' },
  { value: 'vat10', label: 'НДС 10 %' },
  { value: 'vat20', label: 'НДС 20 %' },
  { value: 'vat22', label: 'НДС 22 %' },
  { value: 'vat105', label: 'НДС 5/105 (расчётная)' },
  { value: 'vat107', label: 'НДС 7/107 (расчётная)' },
  { value: 'vat110', label: 'НДС 10/110 (расчётная)' },
  { value: 'vat120', label: 'НДС 20/120 (расчётная)' },
  { value: 'vat122', label: 'НДС 22/122 (расчётная)' },
]

const OUTDATED_VAT_LABELS: Readonly<Record<string, string>> = { vat118: 'НДС 18/118 (устаревшая)' }

/** Предмет расчёта, тег 1212. 28 и 29 в ФФД не описаны — их нет в выборе. */
const SUBJECT_OPTIONS: readonly CashOption[] = [
  [1, 'Товар'],
  [2, 'Подакцизный товар'],
  [3, 'Работа'],
  [4, 'Услуга'],
  [5, 'Ставка азартной игры'],
  [6, 'Выигрыш азартной игры'],
  [7, 'Лотерейный билет'],
  [8, 'Выигрыш лотереи'],
  [9, 'Предоставление РИД'],
  [10, 'Платёж'],
  [11, 'Агентское вознаграждение'],
  [12, 'Выплата'],
  [13, 'Иной предмет расчёта'],
  [14, 'Имущественное право'],
  [15, 'Внереализационный доход'],
  [16, 'Иные платежи и взносы'],
  [17, 'Торговый сбор'],
  [18, 'Туристический (курортный) сбор'],
  [19, 'Залог'],
  [20, 'Расход'],
  [21, 'Взносы на пенсионное страхование ИП'],
  [22, 'Взносы на пенсионное страхование'],
  [23, 'Взносы на медицинское страхование ИП'],
  [24, 'Взносы на медицинское страхование'],
  [25, 'Взносы на социальное страхование'],
  [26, 'Платёж казино'],
  [27, 'Выдача денег банковским платёжным агентом'],
  [30, 'Подакцизный товар с обязательной маркировкой, без кода (АТНМ)'],
  [31, 'Подакцизный товар с обязательной маркировкой, с кодом (АТМ)'],
  [32, 'Товар с обязательной маркировкой, без кода (ТНМ)'],
  [33, 'Товар с обязательной маркировкой, с кодом (ТМ)'],
].map(([value, label]) => ({ value: String(value), label: String(label) }))

/** Способ расчёта, тег 1214. */
const METHOD_OPTIONS: readonly CashOption[] = [
  [1, 'Предоплата 100 %'],
  [2, 'Частичная предоплата'],
  [3, 'Аванс'],
  [4, 'Полный расчёт'],
  [5, 'Частичный расчёт и кредит'],
  [6, 'Передача в кредит'],
  [7, 'Оплата кредита'],
].map(([value, label]) => ({ value: String(value), label: String(label) }))

const WEIGHT_OPTIONS: readonly CashOption[] = [
  { value: 'false', label: 'Штучный' },
  { value: 'true', label: 'Весовой' },
]

/** Кассовые поля формы в порядке показа и в порядке сервера (`cashMissing`). */
export const CASH_FIELD_KEYS = ['vatRate', 'unitCode', 'unitName', 'isWeighted', 'paymentSubject', 'paymentMethod'] as const
export type CashFieldKey = (typeof CASH_FIELD_KEYS)[number]

export const CASH_FIELD_TITLES: Readonly<Record<CashFieldKey, string>> = {
  vatRate: 'Ставка НДС',
  unitCode: 'Код единицы (ОКЕИ)',
  unitName: 'Имя единицы',
  isWeighted: 'Весовой признак',
  paymentSubject: 'Предмет расчёта',
  paymentMethod: 'Способ расчёта',
}

const OPTIONS: Readonly<Record<'vatRate' | 'isWeighted' | 'paymentSubject' | 'paymentMethod', readonly CashOption[]>> = {
  vatRate: VAT_OPTIONS,
  isWeighted: WEIGHT_OPTIONS,
  paymentSubject: SUBJECT_OPTIONS,
  paymentMethod: METHOD_OPTIONS,
}

/**
 * Список выбора поля. Значение, которого нет в списке (устаревшая ставка, чужой код), добавляется в конец с пометкой и не
 * подменяется похожим: человек видит, что сохранено на самом деле.
 */
export function optionsFor(field: keyof typeof OPTIONS, current: string): readonly CashOption[] {
  const known = OPTIONS[field]
  if (current === '' || known.some((option) => option.value === current)) return known
  const outdated = field === 'vatRate' ? OUTDATED_VAT_LABELS[current] : undefined
  return [...known, { value: current, label: outdated ?? `Код «${current}» (нет в списке)` }]
}

/** Значение кассового поля словами: пустое — «Не знаю». Единица и её имя — как введены. */
export function cashValueText(form: ProductForm, field: CashFieldKey): string {
  const value = form[field].trim()
  if (value === '') return UNKNOWN_LABEL
  if (field === 'unitCode' || field === 'unitName') return value
  return optionsFor(field, value).find((option) => option.value === value)?.label ?? value
}

/** Кассовые поля, которые не определены (в порядке показа). */
export function undefinedCashFields(form: ProductForm): CashFieldKey[] {
  return CASH_FIELD_KEYS.filter((field) => form[field].trim() === '')
}

/** Значение поля формы для показа в «было / сейчас / ваше» и пометках: у кассового поля — слова, пустое — «Не знаю»; иное поле — как есть. */
export function shownValue(field: ProductFieldKey, raw: string): string {
  if (!(CASH_FIELD_KEYS as readonly string[]).includes(field)) return raw
  const value = raw.trim()
  if (value === '') return UNKNOWN_LABEL
  if (field === 'unitCode' || field === 'unitName') return value
  const selectField = field as 'vatRate' | 'isWeighted' | 'paymentSubject' | 'paymentMethod'
  return optionsFor(selectField, value).find((option) => option.value === value)?.label ?? value
}
